import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Search,
  Filter,
  Play,
  FileText,
  Image,
  File,
  Music,
  ExternalLink,
  Eye,
  Calendar,
  Edit,
  X,
  Save,
  Loader,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import ContentCard from "./ContentCard";
import { toast } from "react-toastify";
import adminService from "../../../services/api/admin.service";
// Main component
function ContentGallery() {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  // Filters state
  const [filters, setFilters] = useState({
    contentType: "",
    category: "",
    status: "",
    tag: "",
    search: "",
    sortOrder: "desc",
  });

  // Search input state (separate from filters to avoid unnecessary API calls)
  const [searchInput, setSearchInput] = useState("");

  // Editing state
  const [editingContent, setEditingContent] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Fetch data from API
  const fetchContents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Call the service method with current pagination and filters
      const data = await adminService.fetchAllContent({
        page: pagination.page,
        limit: pagination.limit,
        filters, // pass the filters object directly
      });

      // Assuming data has the shape: { success: true, data: [...], pagination: {...} }
      if (data.success) {
        setContents(data.data);
        setPagination(data.pagination);
      } else {
        setError(data.message || "Failed to fetch content");
      }
    } catch (err) {
      setError(typeof err === "string" ? err : err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, filters, pagination.limit]);
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      handleFilterChange("search", searchInput);
    }, 500); // debounce delay in ms

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to page 1 when filters change
  };

  // Handle search input
  // const handleSearch = (e) => {
  //   if (e.key === "Enter") {
  //     handleFilterChange("search", searchInput);
  //   }
  // };

  // Submit search on button click
  // const handleSearchSubmit = () => {
  //   handleFilterChange("search", searchInput);
  // };

  // Pagination controls
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  // Manual refresh function
  const handleRefresh = () => {
    fetchContents();
  };

  // Handle authentication error
  const handleLoginRedirect = () => {
    // Redirect to login page
    window.location.href = "/Signin-Signup";
  };

  // Start editing a content item
  const handleEditClick = (content) => {
    setEditingContent({
      ...content,
      _formFile: null,
    });
    setUpdateError(null);
    setUpdateSuccess(false);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingContent(null);
    setUpdateError(null);
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingContent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditingContent((prev) => ({
        ...prev,
        _formFile: file,
      }));
    }
  };

  // Handle content deletion
  const handleDeleteContent = async (contentId) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token)
        throw new Error("Authentication token not found. Please log in.");

      await adminService.deleteTextContent(contentId, token);

      // Optimistic update - remove the content immediately
      setContents((prev) => {
        const newContents = prev.filter((item) => item._id !== contentId);

        // Adjust pagination if needed
        if (newContents.length === 0 && pagination.page > 1) {
          setPagination((prevPagination) => ({
            ...prevPagination,
            page: prevPagination.page - 1,
          }));
          // Fetch the previous page's contents
          fetchContents();
        }

        return newContents;
        toast.success("Content deleted successfully");
      });

      // Update pagination totals
      setPagination((prev) => ({
        ...prev,
        total: prev.total - 1,
        totalPages: Math.ceil((prev.total - 1) / prev.limit),
      }));
    } catch (error) {
      console.error("Failed to delete content:", error);
      // Revert the optimistic update if the deletion fails
      fetchContents(); // Refetch to restore the correct state
    }
  };

  // Handle form submission
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await adminService.updateTextContent(
        editingContent._id,
        editingContent,
        token
      );

      setContents((prev) =>
        prev.map((item) =>
          item._id === editingContent._id ? response.data : item
        )
      );

      setUpdateSuccess(true);
      setTimeout(() => {
        setEditingContent(null);
        setUpdateSuccess(false);
      }, 1500);
    } catch (err) {
      setUpdateError(
        err.message ||
          err.response?.data?.message ||
          "Failed to update content."
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Fixed Header with filters - improved sticky header with no gap */}
      <div className="sticky top-0 z-20 bg-white shadow-md">
        <div className="container mx-auto p-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold">Content Gallery</h1>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-1 bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-2">
            {/* Improved Search bar with button */}
            <div className="relative flex-grow flex">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search content..."
                className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <select
                value={filters.contentType}
                onChange={(e) =>
                  handleFilterChange("contentType", e.target.value)
                }
                className="border rounded-lg px-4 py-2 bg-white"
              >
                <option value="">All Types</option>
                <option value="video">Video</option>
                <option value="paragraph">Paragraph</option>
                <option value="image">Image</option>
                <option value="document">Document</option>
                <option value="audio">Audio</option>
                <option value="videoLink">Video Link</option>
                <option value="other">Other</option>
              </select>

              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="border rounded-lg px-4 py-2 bg-white"
              >
                <option value="">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>

              <select
                value={filters.sortOrder}
                onChange={(e) =>
                  handleFilterChange("sortOrder", e.target.value)
                }
                className="border rounded-lg px-4 py-2 bg-white"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold">Edit Content</h2>
              <button
                onClick={handleCancelEdit}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateSubmit} className="p-4">
              {updateError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {updateError}
                </div>
              )}

              {updateSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                  Content updated successfully!
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={editingContent.title}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={editingContent.description || ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={editingContent.status}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Content type specific fields */}
              {editingContent.contentType === "paragraph" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paragraph Content
                  </label>
                  <textarea
                    name="paragraphContent"
                    value={editingContent.paragraphContent || ""}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="6"
                    required
                  />
                </div>
              )}

              {editingContent.contentType === "videoLink" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Video Link
                    </label>
                    <input
                      type="url"
                      name="videoLink"
                      value={editingContent.videoLink || ""}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      name="paragraphContent"
                      value={editingContent.paragraphContent || ""}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="4"
                    />
                  </div>
                </>
              )}

              {["video", "image", "document", "audio", "other"].includes(
                editingContent.contentType
              ) && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File{" "}
                    {editingContent._formFile
                      ? "(New file selected)"
                      : "(Optional)"}
                  </label>
                  <div className="flex items-center gap-2">
                    {editingContent.fileUrl && !editingContent._formFile && (
                      <div className="text-sm text-gray-600 mb-2">
                        Current file: {editingContent.fileUrl.split("/").pop()}
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="w-full p-2 border rounded"
                  />
                </div>
              )}

              {editingContent.contentType === "other" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Text Content (Optional)
                  </label>
                  <textarea
                    name="paragraphContent"
                    value={editingContent.paragraphContent || ""}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="4"
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  name="_tagsInput"
                  value={
                    editingContent.tags ? editingContent.tags.join(", ") : ""
                  }
                  onChange={(e) => {
                    const tagsArray = e.target.value
                      ? e.target.value
                          .split(",")
                          .map((tag) => tag.trim())
                          .filter(Boolean)
                      : [];
                    setEditingContent((prev) => ({
                      ...prev,
                      tags: tagsArray,
                    }));
                  }}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="tag1, tag2, tag3"
                />
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateLoading}
                  className="inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {updateLoading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main content - directly below header with no gap */}
      <div className="container mx-auto flex-grow p-4 mt-0">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p>{error}</p>
            {error.includes("Authentication") && (
              <button
                onClick={handleLoginRedirect}
                className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Go to Login
              </button>
            )}
          </div>
        ) : contents.length === 0 ? (
          <div className="text-center p-8">
            <p className="text-lg text-gray-500">No content found</p>
            <button
              onClick={handleRefresh}
              className="mt-4 flex items-center gap-1 mx-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw size={16} /> Refresh Content
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contents.map((content) => (
              <ContentCard
                key={content._id}
                content={content}
                onEdit={handleEditClick}
                onDelete={handleDeleteContent}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="flex justify-center mt-8 gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-4 py-2 border rounded-lg flex items-center gap-1 disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4" /> Prev
            </button>
            <div className="flex items-center gap-1">
              <span className="px-4 py-2">
                Page {pagination.page} of {pagination.totalPages}
              </span>
            </div>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="px-4 py-2 border rounded-lg flex items-center gap-1 disabled:opacity-50"
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
export default ContentGallery;
